import { IMAGES } from './images'

export interface GalleryImage {
  id: string
  src: string
  /** The actual alt string — describes the picture, never the filename. */
  alt: string
  /** Used by the home-page teaser only; the gallery collage shows no text. */
  caption: string
  /** Tiles at double size in the collage. Three of these, no more. */
  feature?: boolean
}

/**
 * The café's own photographs. Every stock stand-in that used to live here
 * has been replaced — nothing in the gallery is a picture of somewhere
 * else pretending to be this place.
 *
 * Ordered as a walk rather than by category: come up the hill, in at the
 * door, up to the bar, then room by room through the building, out to the
 * hatch and the ice cream counter, onto the deck, and finish on a Friday
 * night.
 *
 * The interior is most of the collage now, and rightly so: the inside of
 * this building is the reason people photograph it. The three features are
 * the pictures that carry the most information about what the place is —
 * the bar, the view, and a Friday.
 *
 * Deliberately includes second angles on the same rooms. On a page whose
 * only job is looking at photographs, a second view of the snug earns its
 * place in a way it would not on a page with something to do.
 */
export const galleryImages: GalleryImage[] = [
  {
    id: 'building-from-road',
    ...IMAGES.buildingFromRoad,
    caption: 'How you first see us, coming up the hill.',
  },
  {
    id: 'front-door',
    ...IMAGES.frontDoor,
    caption: 'The door, under the sign.',
  },
  {
    id: 'gable-motorcycle',
    ...IMAGES.gableMotorcycle,
    caption: 'A Harley up on the gable end, because why not.',
  },
  {
    id: 'the-bar',
    ...IMAGES.theBar,
    caption: 'The bar, guitars made from bike chain and all.',
    feature: true,
  },
  {
    id: 'bar-front',
    ...IMAGES.barFront,
    caption: 'Two guitars made from bike chain, and the roundel between them.',
  },
  {
    id: 'bar-square-on',
    ...IMAGES.barSquareOn,
    caption: 'Both banks of taps, and everything behind them.',
  },
  {
    id: 'bar-length',
    ...IMAGES.barLength,
    caption: 'Mind your head and the step.',
  },
  {
    id: 'drinks-board',
    ...IMAGES.drinksBoard,
    caption: 'The coffee bar, and the board that runs it.',
  },
  {
    id: 'drinks-board-prices',
    ...IMAGES.drinksBoardPrices,
    caption: 'Everything, and what it costs.',
  },
  {
    id: 'cake-cabinet',
    ...IMAGES.cakeCabinet,
    caption: 'Six to choose between, all £3.50.',
  },
  {
    id: 'cake-slice',
    ...IMAGES.cakeSlice,
    caption: 'Cut thick, as it should be.',
  },
  {
    id: 'cake-blueberry-swirl',
    ...IMAGES.cakeBlueberrySwirl,
    caption: 'Blueberry, under a swirl of cream cheese.',
  },
  {
    id: 'cake-blueberry-bakewell',
    ...IMAGES.cakeBlueberryBakewell,
    caption: 'Blueberry bakewell, dusted over.',
  },
  {
    id: 'dining-room',
    ...IMAGES.diningRoom,
    caption: 'Tartan underfoot, motorcycles overhead.',
  },
  {
    id: 'dining-room-high',
    ...IMAGES.diningRoomHigh,
    caption: 'The long room, from the top of the steps.',
  },
  {
    id: 'mounted-motorcycle',
    ...IMAGES.mountedMotorcycle,
    caption: 'One of the bikes up on the wall.',
  },
  {
    id: 'coffee-sign-room',
    ...IMAGES.coffeeSignRoom,
    caption: 'Fresh brewed coffee, served daily.',
  },
  {
    id: 'bp-room',
    ...IMAGES.bpRoom,
    caption: 'A circus sign in the inglenook.',
  },
  {
    id: 'poster-room',
    ...IMAGES.posterRoom,
    caption: 'Kodak lanterns and an Aintree Grand Prix.',
  },
  {
    id: 'poster-room-wide',
    ...IMAGES.posterRoomWide,
    caption: 'Dubbleware overalls, and a bicycle on the wall.',
  },
  {
    id: 'bus-blind-room',
    ...IMAGES.busBlindRoom,
    caption: 'Clapham Common, Battersea Park, Sloane Square.',
  },
  {
    id: 'window-seat',
    ...IMAGES.windowSeat,
    caption: 'The window seat, and two monkeys in Tudor dress.',
  },
  {
    id: 'snug-fireside',
    ...IMAGES.snugFireside,
    caption: 'The fireside chairs, and a milk churn for a table.',
  },
  {
    id: 'snug-doorway',
    ...IMAGES.snugDoorway,
    caption: 'The same two chairs, from the doorway.',
  },
  {
    id: 'wood-burner-snug',
    ...IMAGES.woodBurnerSnug,
    caption: 'The burner, and a stack of board games.',
  },
  {
    id: 'bullshit-corner',
    ...IMAGES.bullshitCorner,
    caption: 'Welcome to Bullshit Corner.',
  },
  {
    id: 'bar-stools-nook',
    ...IMAGES.barStoolsNook,
    caption: 'Four stools and an old bus blind.',
  },
  {
    id: 'bus-blind-nook',
    ...IMAGES.busBlindNook,
    caption: 'Through the arch to the coffee bar.',
  },
  {
    id: 'beamed-passage',
    ...IMAGES.beamedPassage,
    caption: 'Mind your head through here.',
  },
  {
    id: 'antiques-passage',
    ...IMAGES.antiquesPassage,
    caption: 'Something to read, if you are stopping a while.',
  },
  {
    id: 'upper-window-room',
    ...IMAGES.upperWindowRoom,
    caption: 'Up the steps, and a table by the window.',
  },
  {
    id: 'garden-corridor',
    ...IMAGES.gardenCorridor,
    caption: 'This way to the beer garden.',
  },
  {
    id: 'engine-alcove',
    ...IMAGES.engineAlcove,
    caption: 'A Triumph single, lit like a painting.',
  },
  {
    id: 'bp-corner',
    ...IMAGES.bpCorner,
    caption: 'Two chairs, one BP sign.',
  },
  {
    id: 'dunlop-sign',
    ...IMAGES.dunlopSign,
    caption: 'Dunlop, leaning where it landed.',
  },
  {
    id: 'dunlop-room',
    ...IMAGES.dunlopRoom,
    caption: 'The red one, above the radiator.',
  },
  {
    id: 'chess-table',
    ...IMAGES.chessTable,
    caption: 'The chess is always set up.',
  },
  {
    id: 'interior-memorabilia',
    ...IMAGES.interiorMemorabilia,
    caption: 'Thirty years of collecting, hung on one wall.',
  },
  {
    id: 'merchandise-corner',
    ...IMAGES.merchandiseCorner,
    caption: 'Tees, caps and a couple of painted lids.',
  },
  {
    id: 'merchandise-wall',
    ...IMAGES.merchandiseWall,
    caption: 'Low On Fuel Café, Brackenfield.',
  },
  {
    id: 'merchandise-lounge',
    ...IMAGES.merchandiseLounge,
    caption: 'The room the shirts hang in.',
  },
  {
    id: 'mirror-wall',
    ...IMAGES.mirrorWall,
    caption: 'The roundel, reflected back.',
  },
  {
    id: 'mirror-wall-wide',
    ...IMAGES.mirrorWallWide,
    caption: 'Someone left a bear on the shelf.',
  },
  {
    id: 'interior-bar',
    ...IMAGES.interiorBar,
    caption: 'Flagstones, and a bicycle up on the wall.',
  },
  {
    id: 'interior-snug',
    ...IMAGES.interiorSnug,
    caption: 'A table made from a pinball machine.',
  },
  {
    id: 'fuel-stop-hatch',
    ...IMAGES.fuelStopHatch,
    caption: 'The Fuel Stop, boards either side of the hatch.',
  },
  {
    id: 'ice-cream-counter',
    ...IMAGES.iceCreamCounter,
    caption: 'Two cabinets of gelato, scooped to order.',
  },
  {
    id: 'ice-cream-cone',
    ...IMAGES.iceCreamCone,
    caption: 'Best eaten looking that way.',
  },
  {
    id: 'iced-coffees',
    ...IMAGES.icedCoffees,
    caption: 'Iced coffees, and someone else\u2019s Triumph.',
  },
  {
    id: 'deck-view',
    ...IMAGES.deckView,
    caption: 'The deck, and the reason to sit on it.',
    feature: true,
  },
  {
    id: 'helmets-on-table',
    ...IMAGES.helmetsOnTable,
    caption: 'Helmets down, kettle on.',
  },
  {
    id: 'bike-night-crowd',
    ...IMAGES.bikeNightCrowd,
    caption: 'A Friday evening, from the top of the deck.',
    feature: true,
  },
  {
    id: 'bike-night-sign',
    ...IMAGES.bikeNightSign,
    caption: 'Every Friday, without fail.',
  },
]
